import { createFileRoute, Link } from '@tanstack/react-router';
import { useDeferredValue } from 'react';
import { useTranslation } from 'react-i18next';
import { getListRunsQueryOptions, useListRunsSuspense } from '@/api/generated/endpoints';
import { PaginationBar } from '@/components/pagination-bar';
import { RouteErrorFallback } from '@/components/route-error-fallback';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RunsFilters } from '@/features/runs/components/runs-filters';
import { RunsTable } from '@/features/runs/components/runs-table';
import { isRunEditable } from '@/features/runs/utils/run-editability';
import { runsSearchSchema, toListRunsParams } from '@/features/runs/utils/search';

export const Route = createFileRoute('/_authenticated/runs/')({
	validateSearch: runsSearchSchema,
	loaderDeps: ({ search }) => search,
	loader: ({ context: { queryClient }, deps }) => {
		// Fire-and-forget prefetch: the component reads via a suspense query, so the
		// layout-level <Suspense> shows GenericPageSkeleton until data resolves.
		void queryClient.ensureQueryData(getListRunsQueryOptions(toListRunsParams(deps)));
	},
	errorComponent: (props) => <RouteErrorFallback {...props} fallbackMessageKey='runs_error_load' />,
	component: RunsRoute,
});

function RunsRoute() {
	const { t } = useTranslation();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const deferredSearch = useDeferredValue(search);
	const isStale = deferredSearch !== search;

	const runsQuery = useListRunsSuspense(toListRunsParams(deferredSearch), {
		query: { retry: false },
	});

	const runs = runsQuery.data.data;
	const meta = runsQuery.data.meta;

	return (
		<Card>
			<CardHeader className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
				<div>
					<CardTitle>{t('runs_page_title')}</CardTitle>
					<CardDescription>{t('runs_page_description')}</CardDescription>
				</div>

				<Button asChild>
					<Link to='/runs/new'>{t('runs_create_button')}</Link>
				</Button>
			</CardHeader>

			<CardContent className='space-y-4'>
				<RunsFilters
					onApply={() => {
						navigate({
							search: (previous) => ({ ...previous, page: 1 }),
						});
					}}
				/>

				<RunsTable
					runs={runs}
					isStale={isStale}
					actions={(run) => (
						<div className='flex justify-end gap-2'>
							<Button asChild variant='outline' size='sm'>
								<Link to='/runs/$runId' params={{ runId: run.id }}>
									{t('runs_details_button')}
								</Link>
							</Button>
							{isRunEditable(run.status) ? (
								<Button asChild variant='outline' size='sm'>
									<Link to='/runs/$runId/edit' params={{ runId: run.id }}>
										{t('runs_edit_button')}
									</Link>
								</Button>
							) : null}
						</div>
					)}
				/>

				<PaginationBar
					currentPage={search.page}
					totalPages={meta.total_pages}
					onPageChange={(page) => navigate({ search: (previous) => ({ ...previous, page }) })}
				/>
			</CardContent>
		</Card>
	);
}
